import cn from 'classnames';
import { FC, KeyboardEvent, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import styles from './InlineText.module.scss';

type InputProps = {
  value: string;
  label: string;
  className?: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
};

// Enter or leaving the field keeps the text, Escape restores it. An empty
// text is not a name, so it is dropped.
const InlineInput: FC<InputProps> = ({
  value,
  label,
  className,
  onCommit,
  onCancel,
}) => {
  const [text, setText] = useState(value);
  // The input may also blur while it is removed after Enter or Escape.
  const doneRef = useRef(false);
  const end = (commit: boolean) => {
    if (doneRef.current) {
      return;
    }
    doneRef.current = true;
    if (commit && text.trim() && text !== value) {
      onCommit(text);
      return;
    }
    onCancel();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      end(true);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      end(false);
    }
  };

  return (
    <input
      className={cn(styles.input, className)}
      aria-label={label}
      autoFocus
      size={Math.max(text.length, 4)}
      value={text}
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => end(true)}
      onKeyDown={onKeyDown}
    />
  );
};

// The height follows the text, so a long instruction is edited without
// scrolling inside a one-line box.
const fitHeight = (element: HTMLTextAreaElement | null) => {
  if (element) {
    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }
};

type TextAreaProps = InputProps & {
  // Long texts of several paragraphs: Enter starts a new line and
  // Ctrl+Enter (Cmd+Enter) keeps the text.
  multiline?: boolean;
  maxLength?: number;
  // Shown while the pointer rests on the box.
  title?: string;
};

// Like InlineInput, but Shift+Enter starts a new line and an empty text is
// kept: clearing an instruction is an edit, not a cancel.
export const InlineTextArea: FC<TextAreaProps> = ({
  value,
  label,
  className,
  multiline = false,
  maxLength,
  title,
  onCommit,
  onCancel,
}) => {
  const [text, setText] = useState(value);
  const doneRef = useRef(false);
  const end = (commit: boolean) => {
    if (doneRef.current) {
      return;
    }
    doneRef.current = true;
    if (commit && text.trim() !== value.trim()) {
      onCommit(text);
      return;
    }
    onCancel();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const keeps = multiline ? event.ctrlKey || event.metaKey : !event.shiftKey;
    if (event.key === 'Enter' && keeps) {
      event.preventDefault();
      end(true);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      end(false);
    }
  };

  return (
    <textarea
      ref={fitHeight}
      className={cn(styles.textarea, className)}
      aria-label={label}
      autoFocus
      rows={1}
      maxLength={maxLength}
      title={title}
      value={text}
      onFocus={(event) =>
        event.currentTarget.setSelectionRange(
          event.currentTarget.value.length,
          event.currentTarget.value.length,
        )
      }
      onChange={(event) => {
        setText(event.target.value);
        fitHeight(event.currentTarget);
      }}
      onBlur={() => end(true)}
      onKeyDown={onKeyDown}
    />
  );
};

type Props = {
  value: string;
  label: string;
  editable: boolean;
  className?: string;
  // A new section or field opens with its name ready to type.
  startEditing?: boolean;
  onCommit: (value: string) => void;
  onEditEnd?: () => void;
};

// A name edited where it is shown: clicking the text turns it into an input
// with the same typography.
export const InlineText: FC<Props> = ({
  value,
  label,
  editable,
  className,
  startEditing = false,
  onCommit,
  onEditEnd,
}) => {
  const { formatMessage } = useIntl();
  const [editing, setEditing] = useState(startEditing);
  const stop = () => {
    setEditing(false);
    onEditEnd?.();
  };

  if (!editable) {
    return <span className={className}>{value}</span>;
  }
  if (editing) {
    return (
      <InlineInput
        value={value}
        label={label}
        className={className}
        onCommit={(text) => {
          stop();
          onCommit(text);
        }}
        onCancel={stop}
      />
    );
  }
  return (
    <button
      type="button"
      className={cn(styles.text, className)}
      title={formatMessage({
        id: 'workspace.template.rename',
        defaultMessage: 'Click to rename',
      })}
      onClick={() => setEditing(true)}
    >
      {value}
    </button>
  );
};
